import { type ChosenFile, type InferResponseType, putFile, unwrap, withSignal } from "@nexxt/api-client";
import { filterQuery } from "@nexxt/app-kit";
import { CLAIMS_FOLDER, type ListCustomerClaimsInput, type OpenClaimInput } from "@nexxt/contracts/claims";
import { infiniteQueryOptions, queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api";

// Server state of the customer's claims (B-55, ADR-225, ADR-228): one factory, the same keys for the loaders and the
// screens; the claim the customer opens, with its photos uploaded first into the folder of the claims.

const claims = api.claims.claims;

type ListPage = InferResponseType<typeof claims.$get, 200>;
export type CustomerClaimSummary = ListPage["items"][number];
export type CustomerClaim = InferResponseType<(typeof claims)[":claimId"]["$get"], 200>;

/** The filters of the list (`listCustomerClaimsInput`), without the paging the query adds. */
export type ClaimFilters = Pick<ListCustomerClaimsInput, "salesOrder">;

const PAGE_SIZE = 25;

const keys = {
  all: ["claims"] as const,
  lists: ["claims", "list"] as const,
};

export const claimQueries = {
  ...keys,

  list: (filters: ClaimFilters) =>
    infiniteQueryOptions({
      queryKey: [...keys.lists, filters],
      queryFn: ({ pageParam, signal }) =>
        unwrap(
          claims.$get(
            { query: { ...filterQuery(filters), page: String(pageParam), pageSize: String(PAGE_SIZE) } },
            withSignal(signal),
          ),
        ),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    }),

  /** A claim with its photos; 404 for one of another customer. */
  detail: (claimId: string) =>
    queryOptions({
      queryKey: [...keys.all, "detail", claimId],
      queryFn: ({ signal }) => unwrap(claims[":claimId"].$get({ param: { claimId } }, withSignal(signal))),
    }),
};

/** Where a photo or a file of a claim is downloaded, as an attachment (the same origin, with the session cookie). */
export const attachmentHref = (claimId: string, fileId: string): string =>
  claims[":claimId"].files[":fileId"].$url({ param: { claimId, fileId } }).href;

/** What the form sends: the claim, and the files chosen, each with the id it is stored under. */
export interface ClaimToOpen extends Omit<OpenClaimInput, "fileIds"> {
  files: ChosenFile[];
}

/**
 * The photos to the folder of the claims, one after the other (the gateway counts the uploads of a minute), then
 * `claims.claim.open` naming them. The ids are given once, at the choice, and the claim's when the form opened: a
 * retry stores the same files again and answers the same claim, never a second ticket.
 */
export function useOpenClaim(onUploaded: (done: number) => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ files, ...claim }: ClaimToOpen) => {
      for (const [done, chosen] of files.entries()) {
        onUploaded(done);
        await putFile(api.files.files[":fileId"], chosen, CLAIMS_FOLDER);
      }
      onUploaded(files.length);
      return unwrap(claims.$post({ json: { ...claim, fileIds: files.map((f) => f.fileId) } }));
    },
    onSuccess: (claim) => qc.setQueryData(claimQueries.detail(claim.id).queryKey, claim),
    onSettled: () => qc.invalidateQueries({ queryKey: keys.lists }),
  });
}
